import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { connectionListSchema } from "@zworks/console-contracts";
import { Link } from "react-router";
import { StatusBadge } from "@/components/bespoke/status-badge";
import { Button } from "@/components/ui/button";
import { apiGet, runsQuery } from "@/lib/api";

/** Additional attention sources remain project-scoped and never turn an error into zero. */
export function ZotWorkAttention({ projectId }: { projectId: string }) {
  const failed = useQuery(runsQuery(projectId, "failed", 1));
  const waiting = useQuery(runsQuery(projectId, "waiting", 1));
  const connections = useQuery({
    queryKey: ["projects", projectId, "connections"],
    queryFn: ({ signal }) =>
      apiGet(
        `/api/v1/projects/${encodeURIComponent(projectId)}/connections`,
        connectionListSchema,
        signal,
      ),
  });
  const [now] = useState(() => Date.now());
  const expiring = (connections.data ?? []).filter(
    (connection) =>
      connection.status !== "revoked" &&
      connection.expiresAt !== null &&
      Date.parse(connection.expiresAt) <= now + 7 * 86_400_000,
  );
  const items = [
    ...(failed.data?.runs ?? []).map((run) => ({
      id: `failed-${run.id}`,
      title: run.workItemTitle ?? `Run ${run.id.slice(0, 8)}`,
      label: "Failed run",
      status: "failed" as const,
      href: `/runs/${run.id}`,
    })),
    ...(waiting.data?.runs ?? []).map((run) => ({
      id: `waiting-${run.id}`,
      title: run.workItemTitle ?? `Run ${run.id.slice(0, 8)}`,
      label: "Waiting for input",
      status: "waiting" as const,
      href: `/runs/${run.id}`,
    })),
    ...expiring.map((connection) => ({
      id: `connection-${connection.id}`,
      title: connection.name,
      label:
        Date.parse(connection.expiresAt!) <= now
          ? "Connection expired"
          : "Connection expires within 7 days",
      status: "pending" as const,
      href: "/integrations",
    })),
  ];
  const unavailable = failed.isError || waiting.isError || connections.isError;
  return (
    <>
      {unavailable ? (
        <p role="status" className="mt-3 text-sm text-muted-foreground">
          Some work or connection alerts could not be loaded.{" "}
          <Link to="/runs" className="underline">
            Check runs
          </Link>{" "}
          or{" "}
          <Link to="/integrations" className="underline">
            connections
          </Link>
          .
        </p>
      ) : null}
      {items.length > 0 ? (
        <ul aria-label="Work and connection alerts" className="mt-3 grid gap-2 sm:grid-cols-2">
          {items.map((item) => (
            <li key={item.id} className="flex flex-col gap-2 rounded-xl border bg-card p-3">
              <StatusBadge kind="generic" status={item.status} label={item.label} />
              <p className="text-sm font-medium">{item.title}</p>
              <Button variant="outline" size="sm" className="self-start" asChild>
                <Link to={item.href}>Open</Link>
              </Button>
            </li>
          ))}
        </ul>
      ) : null}
    </>
  );
}
